import React, { useState } from 'react';
import { Product } from '../../types/admin';
import styles from '../../styles/admin/Dashboard.module.css';
import { categoryOptions } from '../../utils/options';

interface ProductListProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (productId: string) => void;
}

function toTitleCase(str: string) {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const ProductList: React.FC<ProductListProps> = ({
  products,
  onEdit,
  onDelete,
}) => {
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'stock'>('name');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  // Sort and filter products
  const sortedAndFilteredProducts = products
    .filter(
      (p) =>
        filterCategory.toLowerCase() === 'all' || p.category === filterCategory
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.price - b.price;
        case 'stock':
          return a.stock - b.stock;
        default:
          return a.name.localeCompare(b.name);
      }
    });

  // TODO: [FIRESTORE-INTEGRATION]
  // Add real-time listener for product updates
  // Implement batch operations for multiple deletions
  // Add caching for better performance

  const handleDelete = async (productId: string) => {
    onDelete(productId);
  };

  return (
    <div className={styles.productList}>
      <div className={styles.productListControls}>
        <div className={styles.filterSortControls}>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={styles.categoryFilter}
          >
            {categoryOptions.map((category) => (
              <option key={category} value={category}>
                {toTitleCase(category)}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as 'name' | 'price' | 'stock')
            }
            className={styles.sortSelect}
          >
            <option value="name">Sort by Name</option>
            <option value="price">Sort by Price</option>
            <option value="stock">Sort by Stock</option>
          </select>
        </div>

        <div className={styles.productCount}>
          {sortedAndFilteredProducts.length} products
        </div>
      </div>

      <div className={styles.productsGrid}>
        {sortedAndFilteredProducts.map((product) => (
          <div key={product.id} className={styles.productCard}>
            <div className={styles.productImageContainer}>
              <img
                src={product.imageUrl}
                alt={product.name}
                className={styles.productImage}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    '/placeholder-product.png';
                }}
              />
              {product.stock < 5 && (
                <span className={styles.lowStockBadge}>Low Stock</span>
              )}
            </div>

            <div className={styles.productDetails}>
              <h3>{product.name}</h3>
              <p className={styles.productPrice}>${product.price.toFixed(2)}</p>
              <p className={styles.productStock}>Stock: {product.stock}</p>
              <p className={styles.productCategory}>{product.category}</p>
            </div>

            <div className={styles.productActions}>
              <button
                onClick={() => onEdit(product)}
                className={styles.editButton}
                title="Edit product"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(product.id)}
                className={styles.deleteButton}
                title="Delete product"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {sortedAndFilteredProducts.length === 0 && (
        <div className={styles.noProducts}>
          <p>
            No products found. Try adjusting your filters or add some products.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductList;
